/**
 * In-App Micro-Surveys & Exit-Intent Polls (PostHog-style)
 * Encapsulated inside an isolated Shadow DOM container.
 */

export class SurveyManager {
    constructor(options = {}) {
        this.appId = options.appId || '';
        this.serverUrl = (options.serverUrl || '').replace(/\/+$/, '');
        this.visitorId = options.visitorId || '';
        this.sessionId = options.sessionId || '';
        this.activeSurveys = [];
        this._currentSurvey = null;
        this._hostEl = null;
        this._shadow = null;
        this._listeners = [];
        this._hasShown = false;
    }

    /**
     * Fetch active surveys for this website and setup event triggers
     */
    async init() {
        if (!this.appId || typeof window === 'undefined') return;

        try {
            const currentUrl = window.location.href;
            const res = await fetch(
                `${this.serverUrl}/api/widget/surveys/active?appId=${encodeURIComponent(this.appId)}&url=${encodeURIComponent(currentUrl)}`
            );
            if (!res.ok) return;

            const data = await res.json();
            this.activeSurveys = Array.isArray(data.surveys) ? data.surveys : [];

            if (this.activeSurveys.length > 0) {
                this._setupTriggers();
            }
        } catch (err) {
            console.debug('[iGreet Survey] Failed to load active surveys:', err.message);
        }
    }

    /**
     * Wire listeners for each trigger type
     */
    _setupTriggers() {
        for (const survey of this.activeSurveys) {
            // Check local cooldown
            if (this._isSuppressed(survey)) continue;

            const triggerType = survey.triggers?.type || 'exit_intent';

            if (triggerType === 'exit_intent') {
                const onMouseLeave = (e) => {
                    if (e.clientY <= 12 && !this._hasShown && !this._isSuppressed(survey)) {
                        this.show(survey);
                    }
                };
                document.addEventListener('mouseleave', onMouseLeave);
                this._listeners.push(() => document.removeEventListener('mouseleave', onMouseLeave));
            } else if (triggerType === 'delay') {
                const delaySec = survey.triggers?.delaySeconds || 5;
                const timer = setTimeout(() => {
                    if (!this._hasShown && !this._isSuppressed(survey)) {
                        this.show(survey);
                    }
                }, delaySec * 1000);
                this._listeners.push(() => clearTimeout(timer));
            } else if (triggerType === 'scroll_depth') {
                const targetPercent = survey.triggers?.scrollPercent || 50;
                const onScroll = () => {
                    const scrollH = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
                    const currentPct = Math.round(((window.scrollY || window.pageYOffset || 0) / scrollH) * 100);
                    if (currentPct >= targetPercent && !this._hasShown && !this._isSuppressed(survey)) {
                        this.show(survey);
                        window.removeEventListener('scroll', onScroll);
                    }
                };
                window.addEventListener('scroll', onScroll, { passive: true });
                this._listeners.push(() => window.removeEventListener('scroll', onScroll));
            }
        }
    }

    /**
     * Trigger a post-chat survey manually after a conversation finishes
     */
    triggerPostChat() {
        const postChatSurvey = this.activeSurveys.find(
            (s) => (s.triggers?.type === 'post_chat' || s.type === 'csat') && !this._isSuppressed(s)
        );
        if (postChatSurvey && !this._hasShown) {
            this.show(postChatSurvey);
        }
    }

    _isSuppressed(survey) {
        if (!survey) return false;
        const surveyId = survey._id;
        const freq = survey.triggers?.frequency || 'until_completed';
        const cooldownDays = Number(survey.triggers?.cooldownDays) !== undefined ? Number(survey.triggers?.cooldownDays) : 1;
        const cooldownMs = Math.max(0, cooldownDays * 24 * 60 * 60 * 1000);

        // 'always' -> never suppress (useful for test/dev)
        if (freq === 'always') return false;

        try {
            const completedAtStr = localStorage.getItem(`igreet_survey_done_${surveyId}`);
            if (completedAtStr) {
                if (freq === 'once_per_user' || freq === 'until_completed') {
                    return true; // permanently completed
                }
                if (freq === 'recurring') {
                    const completedAt = Number(completedAtStr) || 0;
                    if (Date.now() - completedAt < cooldownMs) return true;
                }
            }

            const dismissedAtStr = localStorage.getItem(`igreet_survey_dismiss_${surveyId}`);
            if (dismissedAtStr) {
                if (freq === 'once_per_user') {
                    return true; // dismissed once -> never show again
                }
                const dismissedAt = Number(dismissedAtStr) || 0;
                if (Date.now() - dismissedAt < cooldownMs) return true;
            }
        } catch {}
        return false;
    }

    _suppress(surveyId, type = 'done') {
        try {
            if (type === 'done') {
                localStorage.setItem(`igreet_survey_done_${surveyId}`, String(Date.now()));
            } else {
                localStorage.setItem(`igreet_survey_dismiss_${surveyId}`, String(Date.now()));
            }
        } catch {}
    }

    /**
     * Render the micro-survey card in Shadow DOM
     */
    show(survey) {
        if (this._hasShown || !survey) return;
        this._hasShown = true;
        this._currentSurvey = survey;

        // Create or reuse host element
        if (!this._hostEl) {
            this._hostEl = document.createElement('div');
            this._hostEl.id = 'igreet-survey-host';
            document.body.appendChild(this._hostEl);
            this._shadow = this._hostEl.attachShadow({ mode: 'open' });
        }

        const primaryColor = survey.appearance?.themeColor || '#2563eb';
        const position = survey.appearance?.position || 'bottom_right';

        const posStyle = position === 'bottom_left'
            ? 'bottom: 24px; left: 24px;'
            : position === 'top_banner'
            ? 'top: 20px; left: 50%; transform: translateX(-50%);'
            : position === 'center_modal'
            ? 'top: 50%; left: 50%; transform: translate(-50%, -50%);'
            : 'bottom: 24px; right: 24px;';

        const styles = `
            :host {
                all: initial;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                z-index: 2147483640;
                position: fixed;
                ${posStyle}
            }
            * {
                box-sizing: border-box;
                margin: 0;
                padding: 0;
            }
            .survey-card {
                background: #ffffff;
                color: #1e293b;
                width: 360px;
                max-width: calc(100vw - 32px);
                border-radius: 16px;
                box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.06);
                padding: 22px;
                animation: igreet-slide-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                position: relative;
            }
            @keyframes igreet-slide-in {
                from { opacity: 0; transform: translateY(16px) scale(0.96); }
                to { opacity: 1; transform: translateY(0) scale(1); }
            }
            .close-btn {
                position: absolute;
                top: 14px;
                right: 14px;
                background: transparent;
                border: none;
                color: #94a3b8;
                font-size: 18px;
                cursor: pointer;
                line-height: 1;
                padding: 4px;
                border-radius: 6px;
            }
            .close-btn:hover { color: #475569; background: #f1f5f9; }
            .header-tag {
                display: inline-flex;
                align-items: center;
                gap: 5px;
                font-size: 11px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                color: ${primaryColor};
                margin-bottom: 8px;
            }
            .question {
                font-size: 15px;
                font-weight: 700;
                line-height: 1.35;
                color: #0f172a;
                margin-bottom: 6px;
                padding-right: 20px;
            }
            .description {
                font-size: 13px;
                color: #64748b;
                line-height: 1.4;
                margin-bottom: 16px;
            }
            /* NPS 0-10 Grid */
            .nps-grid {
                display: grid;
                grid-template-columns: repeat(11, 1fr);
                gap: 3px;
                margin-bottom: 8px;
            }
            .nps-btn {
                aspect-ratio: 1;
                background: #f8fafc;
                border: 1px solid #e2e8f0;
                border-radius: 6px;
                font-size: 12px;
                font-weight: 600;
                color: #334155;
                cursor: pointer;
                transition: all 0.15s ease;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .nps-btn:hover {
                background: ${primaryColor};
                color: #ffffff;
                border-color: ${primaryColor};
                transform: translateY(-2px);
            }
            .nps-labels {
                display: flex;
                justify-content: space-between;
                font-size: 11px;
                color: #94a3b8;
                font-weight: 500;
                margin-bottom: 14px;
            }
            /* CSAT / Stars */
            .csat-row {
                display: flex;
                justify-content: space-around;
                margin: 12px 0 16px;
            }
            .csat-btn {
                background: #f8fafc;
                border: 1px solid #e2e8f0;
                border-radius: 10px;
                padding: 10px 14px;
                font-size: 22px;
                cursor: pointer;
                transition: all 0.15s ease;
            }
            .csat-btn:hover {
                transform: scale(1.2);
                background: #f1f5f9;
                border-color: #cbd5e1;
            }
            /* Single Choice Options */
            .options-list {
                display: flex;
                flex-direction: column;
                gap: 8px;
                margin-bottom: 14px;
            }
            .option-chip {
                background: #f8fafc;
                border: 1px solid #e2e8f0;
                border-radius: 8px;
                padding: 10px 14px;
                font-size: 13px;
                font-weight: 500;
                color: #334155;
                cursor: pointer;
                text-align: left;
                transition: all 0.15s ease;
            }
            .option-chip:hover {
                background: #eff6ff;
                border-color: ${primaryColor};
                color: ${primaryColor};
            }
            .option-chip.selected {
                background: ${primaryColor};
                color: #ffffff;
                border-color: ${primaryColor};
            }
            /* Feedback Textarea */
            .feedback-box {
                width: 100%;
                border: 1px solid #cbd5e1;
                border-radius: 8px;
                padding: 10px 12px;
                font-size: 13px;
                font-family: inherit;
                color: #1e293b;
                resize: none;
                height: 70px;
                margin-bottom: 12px;
                outline: none;
            }
            .feedback-box:focus {
                border-color: ${primaryColor};
                box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
            }
            .submit-btn {
                width: 100%;
                background: ${primaryColor};
                color: #ffffff;
                border: none;
                border-radius: 8px;
                padding: 10px 16px;
                font-size: 13px;
                font-weight: 600;
                cursor: pointer;
                transition: opacity 0.15s;
            }
            .submit-btn:hover { opacity: 0.92; }
            .thank-you {
                text-align: center;
                padding: 20px 0;
            }
            .thank-icon {
                font-size: 38px;
                margin-bottom: 10px;
            }
            .thank-title {
                font-size: 16px;
                font-weight: 700;
                color: #0f172a;
                margin-bottom: 4px;
            }
            .thank-sub {
                font-size: 13px;
                color: #64748b;
            }
        `;

        const renderForm = () => {
            if (survey.type === 'nps') {
                return `
                    <div class="nps-grid">
                        ${Array.from({ length: 11 }, (_, i) => `<button class="nps-btn" data-val="${i}">${i}</button>`).join('')}
                    </div>
                    <div class="nps-labels">
                        <span>Not likely</span>
                        <span>Very likely</span>
                    </div>
                    <div id="followup-box" style="display: none;">
                        <textarea class="feedback-box" placeholder="What's the main reason for your score? (optional)"></textarea>
                        <button class="submit-btn" id="submit-btn">Send Feedback</button>
                    </div>
                `;
            } else if (survey.type === 'csat' || survey.type === 'rating') {
                const emojis = ['😡', '🙁', '😐', '😊', '😍'];
                return `
                    <div class="csat-row">
                        ${emojis.map((emoji, idx) => `<button class="csat-btn" data-val="${idx + 1}">${emoji}</button>`).join('')}
                    </div>
                    <div id="followup-box" style="display: none;">
                        <textarea class="feedback-box" placeholder="Any suggestions to make it better? (optional)"></textarea>
                        <button class="submit-btn" id="submit-btn">Send Feedback</button>
                    </div>
                `;
            } else if (survey.type === 'single_choice') {
                const opts = survey.options && survey.options.length > 0 ? survey.options : ['Yes', 'No', 'Not sure'];
                return `
                    <div class="options-list">
                        ${opts.map((opt) => `<button class="option-chip" data-val="${opt}">${opt}</button>`).join('')}
                    </div>
                    <div id="followup-box" style="display: none;">
                        <textarea class="feedback-box" placeholder="Anything else you'd like to add? (optional)"></textarea>
                        <button class="submit-btn" id="submit-btn">Submit</button>
                    </div>
                `;
            } else {
                return `
                    <textarea class="feedback-box" placeholder="Type your response here..."></textarea>
                    <button class="submit-btn" id="submit-btn">Submit Feedback</button>
                `;
            }
        };

        this._shadow.innerHTML = `
            <style>${styles}</style>
            <div class="survey-card" id="card">
                <button class="close-btn" id="close-btn" title="Dismiss">✕</button>
                <div class="header-tag">✨ Quick Feedback</div>
                <div class="question">${survey.question}</div>
                ${survey.description ? `<div class="description">${survey.description}</div>` : ''}
                <div id="survey-content">
                    ${renderForm()}
                </div>
            </div>
        `;

        // Wire event handlers
        let selectedValue = null;
        const followupBox = this._shadow.getElementById('followup-box');
        const submitBtn = this._shadow.getElementById('submit-btn');
        const closeBtn = this._shadow.getElementById('close-btn');

        closeBtn.addEventListener('click', () => {
            this._suppress(survey._id, 'dismiss');
            this.destroy();
        });

        // NPS & CSAT & Single Choice select handler
        this._shadow.querySelectorAll('.nps-btn, .csat-btn, .option-chip').forEach((btn) => {
            btn.addEventListener('click', () => {
                selectedValue = btn.getAttribute('data-val');
                this._shadow.querySelectorAll('.nps-btn, .csat-btn, .option-chip').forEach((b) => b.classList.remove('selected'));
                btn.classList.add('selected');

                if (followupBox) {
                    followupBox.style.display = 'block';
                } else {
                    this._submitResponse(survey, selectedValue, '');
                }
            });
        });

        if (submitBtn) {
            submitBtn.addEventListener('click', () => {
                const textarea = this._shadow.querySelector('.feedback-box');
                const comment = textarea ? textarea.value.trim() : '';
                const finalVal = selectedValue !== null ? selectedValue : comment;
                this._submitResponse(survey, finalVal, comment);
            });
        }
    }

    /**
     * Send survey response to backend
     */
    async _submitResponse(survey, responseVal, comment) {
        if (responseVal === null || responseVal === undefined) return;

        this._suppress(survey._id, 'done');

        // Show thank you message
        const contentEl = this._shadow.getElementById('survey-content');
        if (contentEl) {
            contentEl.innerHTML = `
                <div class="thank-you">
                    <div class="thank-icon">🎉</div>
                    <div class="thank-title">Thank you!</div>
                    <div class="thank-sub">Your feedback helps us make things better.</div>
                </div>
            `;
        }

        try {
            await fetch(`${this.serverUrl}/api/widget/surveys/${survey._id}/response`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    appId: this.appId,
                    visitorId: this.visitorId,
                    sessionId: this.sessionId,
                    url: window.location.href,
                    device: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop',
                    response: responseVal,
                    feedback: comment,
                }),
            });
        } catch (err) {
            console.debug('[iGreet Survey] Failed to post survey response:', err.message);
        }

        // Auto close after 2.5 seconds
        setTimeout(() => {
            this.destroy();
        }, 2500);
    }

    /**
     * Remove survey from DOM and cleanup listeners
     */
    destroy() {
        this._listeners.forEach((cleanup) => {
            try { cleanup(); } catch {}
        });
        this._listeners = [];

        if (this._hostEl && this._hostEl.parentNode) {
            this._hostEl.parentNode.removeChild(this._hostEl);
        }
        this._hostEl = null;
        this._shadow = null;
    }
}
